import { z } from 'zod'

// Project status
export type ProjectStatus = 'pending' | 'in_progress' | 'review' | 'completed' | 'cancelled'

// Document category
export type DocumentCategory = 'design' | 'contrat' | 'facture' | 'autre'

// Project interface
export interface Project {
  id: string
  created_at: string
  updated_at: string
  nom: string
  description: string | null
  status: ProjectStatus
  client_id: string
  admin_id: string | null
  budget: number | null
  budget_spent: number | null
  date_debut: string | null
  date_fin: string | null
  progression: number // 0-100
  // Relations
  client?: ProjectClient
  admin?: ProjectAdmin
  tasks?: ProjectTask[]
  documents?: ProjectDocument[]
  invoices?: ProjectInvoice[]
  quotes?: ProjectQuote[]
  milestones?: ProjectMilestone[]
  activities?: ProjectActivity[]
}

export interface ProjectClient {
  id: string
  full_name: string
  email: string
  company: string | null
  avatar_url: string | null
}

export interface ProjectAdmin {
  id: string
  full_name: string
  email: string
  avatar_url: string | null
}

export interface ProjectTask {
  id: string
  created_at: string
  updated_at: string
  project_id: string
  titre: string
  description: string | null
  status: 'todo' | 'in_progress' | 'done'
  priority: 'low' | 'medium' | 'high'
  due_date: string | null
  assigned_to: string | null
  ordre: number
  start_date: string | null
  end_date: string | null
  notes: string | null
  progress: number
  assignee?: ProjectAdmin
}

export interface ProjectDocument {
  id: string
  created_at: string
  updated_at: string
  project_id: string
  nom: string
  description: string | null
  type: string
  categorie: DocumentCategory
  url: string
  taille: number
  uploaded_by: string | null
  share_token: string | null
  share_expires_at: string | null
  uploader?: ProjectAdmin
}

export interface ProjectInvoice {
  id: string
  numero: string
  total_ht: number
  total_ttc: number
  statut: 'brouillon' | 'envoyee' | 'en_attente' | 'payee' | 'annulee'
  date_emission: string
  date_echeance: string
}

export interface ProjectQuote {
  id: string
  numero: string
  total_ht: number
  total_ttc: number
  status: 'brouillon' | 'envoye' | 'accepte' | 'refuse' | 'expire'
  date_emission: string
  date_validite: string
}

export interface ProjectMilestone {
  id: string
  created_at: string
  project_id: string
  titre: string
  description: string | null
  date_prevue: string
  date_completee: string | null
  status: 'pending' | 'completed'
  ordre: number
}

export interface ProjectActivity {
  id: string
  created_at: string
  project_id: string
  type: 'status_change' | 'task_created' | 'task_completed' | 'document_added' | 'milestone_completed' | 'comment' | 'invoice_created' | 'quote_created'
  description: string
  user_id: string | null
  metadata: Record<string, unknown> | null
  user?: ProjectAdmin
}

// Project Members
export type ProjectMemberRole = 'owner' | 'editor' | 'viewer'

export interface ProjectMember {
  id: string
  project_id: string
  user_email: string
  user_id: string | null
  role: ProjectMemberRole
  receive_emails: boolean
  added_by: string | null
  added_at: string
  updated_at: string
}

export interface ProjectInvitation {
  id: string
  project_id: string
  email: string
  role: ProjectMemberRole
  token: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  invited_by: string | null
  invited_at: string
  expires_at: string
  accepted_at: string | null
}

export const MEMBER_ROLE_CONFIG: Record<ProjectMemberRole, { label: string; color: 'mint' | 'blue' | 'gray' }> = {
  owner: { label: 'Proprietaire', color: 'mint' },
  editor: { label: 'Editeur', color: 'blue' },
  viewer: { label: 'Lecteur', color: 'gray' },
}

// Filters
export interface ProjectFilters {
  search?: string
  status?: ProjectStatus | null
  clientId?: string | null
  adminId?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

// Form schemas
export const projectSchema = z.object({
  nom: z.string().min(2, 'Le nom doit contenir au moins 2 caracteres'),
  description: z.string().optional().or(z.literal('')),
  status: z.enum(['pending', 'in_progress', 'review', 'completed', 'cancelled']),
  client_id: z.string().uuid('Client invalide'),
  admin_id: z.string().uuid('Admin invalide').optional().nullable(),
  budget: z.number().positive('Le budget doit etre positif').optional().nullable(),
  date_debut: z.string().optional().nullable(),
  date_fin: z.string().optional().nullable(),
})

export type ProjectFormData = z.infer<typeof projectSchema>

export const taskSchema = z.object({
  titre: z.string().min(2, 'Le titre doit contenir au moins 2 caracteres'),
  description: z.string().optional().or(z.literal('')),
  status: z.enum(['todo', 'in_progress', 'done']),
  priority: z.enum(['low', 'medium', 'high']),
  due_date: z.string().optional().nullable(),
  assigned_to: z.string().uuid('Assignee invalide').optional().nullable(),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  progress: z.number().min(0).max(100).optional().nullable(),
})

export type TaskFormData = z.infer<typeof taskSchema>

export const milestoneSchema = z.object({
  titre: z.string().min(2, 'Le titre doit contenir au moins 2 caracteres'),
  description: z.string().optional().or(z.literal('')),
  date_prevue: z.string().min(1, 'La date prevue est requise'),
})

export type MilestoneFormData = z.infer<typeof milestoneSchema>

// Status labels and colors
export const PROJECT_STATUS_CONFIG: Record<ProjectStatus, { label: string; color: 'gray' | 'blue' | 'gold' | 'mint' | 'red' }> = {
  pending: { label: 'En attente', color: 'gray' },
  in_progress: { label: 'En cours', color: 'blue' },
  review: { label: 'En revision', color: 'gold' },
  completed: { label: 'Termine', color: 'mint' },
  cancelled: { label: 'Annule', color: 'red' },
}

export const DOCUMENT_CATEGORY_CONFIG: Record<DocumentCategory, { label: string; color: 'blue' | 'brown' | 'mint' | 'gray' }> = {
  design: { label: 'Design', color: 'blue' },
  contrat: { label: 'Contrat', color: 'brown' },
  facture: { label: 'Facture', color: 'mint' },
  autre: { label: 'Autre', color: 'gray' },
}

export const TASK_PRIORITY_CONFIG: Record<ProjectTask['priority'], { label: string; color: 'gray' | 'gold' | 'red' }> = {
  low: { label: 'Basse', color: 'gray' },
  medium: { label: 'Moyenne', color: 'gold' },
  high: { label: 'Haute', color: 'red' },
}

export const TASK_STATUS_CONFIG: Record<ProjectTask['status'], { label: string; color: 'gray' | 'blue' | 'mint' }> = {
  todo: { label: 'A faire', color: 'gray' },
  in_progress: { label: 'En cours', color: 'blue' },
  done: { label: 'Termine', color: 'mint' },
}
